import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Mail,
  RefreshCw,
  ShieldAlert,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { AnnouncementBody } from "@/features/whats_new/components/AnnouncementBody";
import {
  useOrderMailLog,
  useRetryOrderMail,
  useSendOrderMail,
} from "../hooks/useOrderMailLog";
import type { OrderMailLog, OrderMailStatus } from "../types";

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

const STATUS_META: Record<
  OrderMailStatus,
  { label: string; cls: string; icon: typeof Check }
> = {
  pending: {
    label: "W kolejce",
    cls: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
    icon: Mail,
  },
  sent: {
    label: "Wysłano",
    cls: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    icon: Check,
  },
  failed: {
    label: "Błąd",
    cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    icon: X,
  },
  blocked_foreign_recipient: {
    label: "Wstrzymano",
    cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    icon: ShieldAlert,
  },
  skipped_no_recipients: {
    label: "Brak odbiorcy",
    cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    icon: AlertTriangle,
  },
  skipped_contractor_off: {
    label: "Wyłączone dla firmy",
    cls: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
    icon: Mail,
  },
  skipped_disabled: {
    label: "Wyłączone",
    cls: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
    icon: Mail,
  },
};

function StatusBadge({ status }: { status: OrderMailStatus }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${meta.cls}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

/** Rozwinięty podgląd wysłanej wiadomości — tak, jak trafiła do skrzynki odbiorcy. */
function MailPreview({ entry }: { entry: OrderMailLog }) {
  const Row = ({ label, value }: { label: string; value: string }) => (
    <div className="flex gap-2 text-xs">
      <span className="w-24 shrink-0 text-gray-400 dark:text-gray-500">{label}</span>
      <span className="text-gray-700 dark:text-gray-200 break-all">{value}</span>
    </div>
  );
  return (
    <div className="mt-2 overflow-hidden rounded border border-gray-200 dark:border-gray-700">
      <div className="space-y-1 border-b border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
        <Row label="Temat" value={entry.subject || "—"} />
        <Row label="Do" value={entry.to.length ? entry.to.join(", ") : "—"} />
        {entry.cc.length > 0 && <Row label="DW" value={entry.cc.join(", ")} />}
        {entry.reply_to && <Row label="Odpowiedź do" value={entry.reply_to} />}
      </div>
      <div className="px-3 py-3">
        {entry.body || entry.body_preview ? (
          <AnnouncementBody text={entry.body || entry.body_preview} />
        ) : (
          <span className="text-sm text-gray-400 dark:text-gray-500">
            Treść wiadomości niedostępna dla tego wpisu.
          </span>
        )}
      </div>
    </div>
  );
}

/** What the user should do about this row — the status alone is not actionable. */
function Hint({ entry, contractorId }: { entry: OrderMailLog; contractorId?: string }) {
  if (entry.status === "skipped_no_recipients") {
    return (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        Żadne konto podwykonawcy nie ma widoczności stawek.{" "}
        {contractorId && (
          <Link
            to={`/contractors/${contractorId}`}
            className="text-[var(--color-primary)] underline"
          >
            Nadaj uprawnienie
          </Link>
        )}
      </span>
    );
  }
  if (entry.status === "blocked_foreign_recipient") {
    return (
      <span className="text-xs text-red-600 dark:text-red-400">
        {entry.error || "Adres odbiorcy występuje także poza tym podwykonawcą."} Wysyłki
        nie da się ponowić — najpierw uporządkuj konta.
      </span>
    );
  }
  if (entry.status === "failed") {
    return (
      <span className="text-xs text-red-600 dark:text-red-400">
        {entry.error || "Nieznany błąd wysyłki."} · próba {entry.attempts}
      </span>
    );
  }
  if (entry.status === "skipped_contractor_off") {
    return (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        Powiadomienia mailowe są wyłączone dla tego podwykonawcy — włącz je na jego karcie.
      </span>
    );
  }
  if (entry.changes.length > 0) {
    return (
      <span className="text-xs text-gray-600 dark:text-gray-400">
        {entry.changes.join(" · ")}
      </span>
    );
  }
  return null;
}

interface Props {
  orderId: string;
  contractorId?: string;
  /** Internal roles only — the endpoint returns 403 for contractor accounts. */
  visible: boolean;
  /** Closed/cancelled orders — a "new order" notice no longer makes sense. */
  finished?: boolean;
}

/**
 * Delivery history of the order's notification emails, with a manual retry.
 *
 * Exists because a notification that silently fails is indistinguishable from one
 * that was never meant to be sent — which is exactly how a subcontractor ended up
 * receiving nothing at all and nobody noticing.
 */
export function OrderMailLogSection({
  orderId,
  contractorId,
  visible,
  finished = false,
}: Props) {
  const { data: entries, isLoading } = useOrderMailLog(orderId, visible);
  const retry = useRetryOrderMail(orderId);
  const send = useSendOrderMail(orderId);
  const [expanded, setExpanded] = useState<string | null>(null);

  if (!visible || isLoading || !entries) return null;

  // Orders approved before this feature existed have no rows at all — offer a
  // one-off send instead of an empty section with nothing to act on.
  const neverAnnounced = !entries.some((e) => e.kind === "created");
  const canSendNow = neverAnnounced && !finished;
  if (entries.length === 0 && !canSendNow) return null;

  const sendError = send.isError
    ? ((send.error as { response?: { data?: { errors?: string[] } } })?.response?.data
        ?.errors?.[0] ?? "Nie udało się wysłać powiadomienia.")
    : null;

  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 border-l-4 border-[var(--color-primary)] pl-3">
        Powiadomienia mailowe
      </h2>
      {canSendNow && (
        <div className="mb-2 flex flex-wrap items-center gap-3 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <span className="text-sm text-amber-800 dark:text-amber-200">
            Podwykonawca nie dostał maila o tym zamówieniu.
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => send.mutate()}
            disabled={send.isPending}
            className="ml-auto"
          >
            <Mail className="h-3 w-3 mr-1" />
            {send.isPending ? "Wysyłanie…" : "Wyślij powiadomienie"}
          </Button>
          {sendError && (
            <span className="w-full text-xs text-red-600 dark:text-red-400">{sendError}</span>
          )}
        </div>
      )}
      <div className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
        {entries.map((entry) => {
          const isOpen = expanded === entry.id;
          return (
            <div key={entry.id} className="px-3 py-2.5 flex flex-col gap-1">
              <div
                className="flex flex-wrap items-center gap-x-3 gap-y-1 cursor-pointer"
                onClick={() => setExpanded(isOpen ? null : entry.id)}
              >
                {isOpen ? (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                )}
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {fmtDateTime(entry.sent_at ?? entry.last_attempt_at ?? entry.created_at)}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {entry.kind_display}
                </span>
                <StatusBadge status={entry.status} />
                {entry.to.length > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {entry.to.length} {entry.to.length === 1 ? "adres" : "adresy"}
                    {entry.cc.length > 0 && `, DW ${entry.cc.length}`}
                  </span>
                )}
                {entry.can_retry && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      retry.mutate(entry.id);
                    }}
                    disabled={retry.isPending}
                    className="ml-auto"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Ponów wysyłkę
                  </Button>
                )}
              </div>
              <Hint entry={entry} contractorId={contractorId} />
              {isOpen && <MailPreview entry={entry} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
